import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { CalendarDays, MapPin, ArrowDown } from "lucide-react";
import { IMAGES, CONTACT, BOOKING_URL, FLOWER_LOGO } from "../lib/data";
import { MaskedLine } from "./Chapter";

export default function Hero({ scrollTo }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col overflow-hidden grain" data-testid="hero">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={IMAGES.hero}
          alt="L'espace aquatique du camping Le Lac aux Oiseaux : piscine couverte et pataugeoire à Martiel"
          className="w-full h-[120%] object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-lakedeep/70 via-lake/40 to-lakedeep/85" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 pt-32 pb-10 max-w-7xl mx-auto w-full">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.7 }}
          className="flex flex-wrap items-center gap-3 mb-8"
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-sand/15 backdrop-blur-md border border-sand/25 text-sand text-xs font-semibold tracking-[0.18em] uppercase px-4 py-2">
            <MapPin size={13} /> Martiel · Aveyron
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-sand/15 backdrop-blur-md border border-sand/25 text-sand text-xs font-semibold tracking-[0.18em] uppercase px-4 py-2">
            <CalendarDays size={13} /> {CONTACT.season}
          </span>
          <span className="inline-flex items-center gap-2.5 rounded-full bg-sand text-pine text-xs font-bold tracking-[0.12em] uppercase pl-3 pr-4 py-1.5 shadow-lg" data-testid="hero-flower-badge">
            <img src={FLOWER_LOGO} alt="Flower Campings" className="h-5 w-auto" />
            Membre Flower Campings
          </span>
        </motion.div>

        <h1 className="font-display font-black text-sand leading-[0.92] tracking-tight text-[17vw] sm:text-[13vw] lg:text-[8.5rem]" data-testid="hero-title">
          <MaskedLine delay={0.35}>Le Lac</MaskedLine>
          <MaskedLine delay={0.5}>
            aux <span className="italic font-medium text-gold">Oiseaux</span>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-sand/85 text-base sm:text-lg leading-relaxed"
        >
          Camping 4 étoiles au bord du lac de Bannac — piscine couverte chauffée,
          mini-ferme, restaurant et 150 emplacements entre lac, forêt et bastides médiévales.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap gap-4"
        >
          <a
            href={BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-cta-reserver"
            className="inline-block rounded-full bg-terra text-sand font-semibold px-8 py-4 shadow-xl shadow-terra/30 transition-all duration-300 hover:bg-gold hover:text-ink hover:-translate-y-1"
          >
            Réserver en ligne
          </a>
          <button
            onClick={() => scrollTo("#camping")}
            data-testid="hero-cta-decouvrir"
            className="rounded-full border border-sand/40 text-sand font-semibold px-8 py-4 backdrop-blur-sm transition-all duration-300 hover:bg-sand hover:text-lake hover:-translate-y-1"
          >
            Découvrir le camping
          </button>
        </motion.div>
      </motion.div>

      <motion.button
        onClick={() => scrollTo("#camping")}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
        className="hidden lg:grid absolute bottom-10 right-10 z-10 place-items-center w-12 h-12 rounded-full border border-sand/30 text-sand/80"
        aria-label="Défiler"
        data-testid="hero-scroll-hint"
      >
        <ArrowDown size={18} />
      </motion.button>
    </section>
  );
}
